"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import type {GailGptDictationStatus} from "@/hooks/useGailGptDictation";
import MemoryComposerButton from "../memory/MemoryComposerButton";
import DictationButton from "./DictationButton";
import HighlightedTextarea from "./HighlightedTextarea";
import SendOrStopButton from "./SendOrStopButton";
import type {ComposerTextareaProps} from "./types";

type MobileCompactInputRowProps = Readonly<{
   textarea: ComposerTextareaProps;
   maxRows?: number;
   upload: {
      show: boolean;
      onClick: () => void;
      disabled: boolean;
      hasAttachedFiles: boolean;
   };
   dictation?: {
      show: boolean;
      status: GailGptDictationStatus;
      onToggle: () => void;
      disabled: boolean;
   };
   enableMemory?: boolean;
   send: {
      show: boolean;
      isSubmitting: boolean;
      onCancelStream?: () => void;
      canSubmit: boolean;
      loading: boolean;
      tooltip?: string;
   };
}>;

/** Single-row composer shown on small screens for the compact variant. */
export default function MobileCompactInputRow({
   textarea,
   maxRows,
   upload,
   dictation,
   enableMemory = false,
   send,
}: MobileCompactInputRowProps) {
   const {t: ct} = useTranslation("common");
   const {inputRef, ...textareaProps} = textarea;

   return (
      <div className="flex items-center md:hidden px-2 py-1.5 gap-1" data-testid="chat-input-mobile-row">
         {upload.show && (
            <button
               type="button"
               onClick={upload.onClick}
               disabled={textarea.disabled || upload.disabled}
               className={clsx(
                  "h-8 w-8 p-0 shrink-0 rounded-lg bg-transparent text-800 hover:bg-200 disabled:opacity-50 flex items-center justify-center focus:outline-none transition-colors duration-200",
                  upload.hasAttachedFiles &&
                     "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
               )}
               aria-label={ct("addFiles")}
            >
               <Icon icon="plus" type={ICON_TYPE.LIGHT} />
            </button>
         )}
         <HighlightedTextarea
            {...textareaProps}
            data-testid="chat-input-mobile"
            ref={inputRef}
            rows={1}
            maxRows={maxRows}
            wrapperClassName="flex-1 min-w-0 pb-2"
            className="w-full bg-background text-1000 resize-none focus:outline-none focus:ring-0 border-0 placeholder:text-500 px-1 pt-2 pb-0"
         />
         {enableMemory && <MemoryComposerButton disabled={textarea.disabled} />}
         {dictation?.show && (
            <DictationButton
               status={dictation.status}
               onClick={dictation.onToggle}
               disabled={dictation.disabled}
            />
         )}
         {send.show && (
            <SendOrStopButton
               isSubmitting={send.isSubmitting}
               onCancelStream={send.onCancelStream}
               canSubmit={send.canSubmit}
               loading={send.loading}
               sendTooltip={send.tooltip}
            />
         )}
      </div>
   );
}
